import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';

/**
 * Validation des modifications sensibles par la direction (phase 5, TF-437).
 * Une présence d'une feuille clôturée, la réouverture d'une feuille, une note
 * publiée et l'identité d'un dossier entré en formation ne se modifient plus
 * directement : le pôle demande, la direction approuve ou refuse. Ce qui est
 * sensible, ce qui change et les gestes ouverts viennent de l'API ; les écrans
 * affichent, ils ne décident rien.
 */

export type TypeDemandeModification =
  | 'PRESENCE'
  | 'REOUVERTURE_FEUILLE'
  | 'RESULTAT_ECF'
  | 'NOTE_TEST'
  | 'NOTE_DEVOIR'
  | 'DOSSIER';
export type StatutDemandeModification = 'EN_ATTENTE' | 'APPROUVEE' | 'REFUSEE' | 'RETIREE';
export type FamilleModification = 'PRESENCES' | 'NOTES' | 'ADMINISTRATIF';

export const MOTIF_MIN = 5;
export const MOTIF_MAX = 1000;

export interface ChangementDemande {
  champ: string;
  libelle: string;
  avant: string;
  apres: string;
}

/** Une demande en attente, telle que l'écran qui porte la donnée la montre. */
export interface DemandeEnAttente {
  id: string;
  type: TypeDemandeModification;
  typeLibelle: string;
  demandeLe: string;
  demandePar: string | null;
  demandeParId: string | null;
  motif: string;
  changements: ChangementDemande[];
}

export interface VueDemande {
  id: string;
  type: TypeDemandeModification;
  typeLibelle: string;
  famille: FamilleModification;
  familleLibelle: string;
  statut: StatutDemandeModification;
  statutLibelle: string;
  statutTon: string;
  directe: boolean;
  objet: string;
  apprenant: { id: string; nom: string; prenom: string } | null;
  site: { code: string; nom: string };
  changements: ChangementDemande[];
  motif: string;
  demandePar: string | null;
  demandeParId: string | null;
  demandeLe: string;
  decision: { le: string; par: string | null; motifRefus: string | null } | null;
  lien: { libelle: string; url: string };
  attenteJours: number | null;
  donneeChangee: boolean;
  refusApprobation: string | null;
  refusRefus: string | null;
  refusRetrait: string | null;
}

export interface ReponseModificationsSensibles {
  calculeLe: string;
  compteurs: {
    aTraiter: number;
    plusAncienneJours: number | null;
    parFamille: Record<FamilleModification, number>;
    approuvees: number;
    refusees: number;
    directes: number;
  };
  lignes: VueDemande[];
  options: { familles: { valeur: FamilleModification; libelle: string }[] };
}

export interface ModificationsDuDossier {
  engage: boolean;
  raison: string | null;
  champs: { champ: string; libelle: string }[];
  validationDirecte: boolean;
  enAttente: VueDemande[];
}

export type EntreeDemande =
  | {
      type: 'PRESENCE';
      presenceId: string;
      statut: 'PRESENT' | 'RETARD' | 'ABSENT';
      heureArrivee?: string | null;
      heureDepart?: string | null;
      justifiee?: boolean;
      motifJustification?: string | null;
      motif: string;
    }
  | { type: 'REOUVERTURE_FEUILLE'; seanceId: string; motif: string }
  | { type: 'RESULTAT_ECF'; evaluationId: string; resultat: string; observation: string | null; motif: string }
  | {
      type: 'NOTE_TEST';
      testId: string;
      notes: Record<string, { points: number; commentaire?: string }>;
      commentaire?: string;
      motif: string;
    }
  | { type: 'NOTE_DEVOIR'; correctionId: string; note: number; commentaire: string | null; motif: string }
  | { type: 'DOSSIER'; apprenantId: string; champs: Record<string, unknown>; motif: string };

export interface ResultatDemande {
  demande: VueDemande;
  /** Appliquée aussitôt : la direction a fait le geste elle-même. */
  appliquee: boolean;
  message: string;
}

const CLE = ['modifications-sensibles'];

export function useModificationsSensibles(site: string) {
  return useQuery({
    queryKey: [...CLE, site],
    queryFn: () => appelApi<ReponseModificationsSensibles>(`/modifications-sensibles?site=${encodeURIComponent(site)}`),
    placeholderData: keepPreviousData,
  });
}

export function useModificationsDuDossier(apprenantId: string | undefined, actif = true) {
  return useQuery({
    queryKey: [...CLE, 'dossier', apprenantId],
    queryFn: () => appelApi<ModificationsDuDossier>(`/modifications-sensibles/dossier/${apprenantId ?? ''}`),
    enabled: Boolean(apprenantId) && actif,
  });
}

/**
 * Une demande ou une décision change la donnée là où elle se lit : la
 * feuille, l'ECF, la copie, le dossier, et le pipeline de la direction.
 */
function useApresGeste() {
  const client = useQueryClient();
  return () => {
    for (const cle of [
      CLE,
      ['financements', 'tableau-de-bord-direction'],
      ['emargement'],
      ['absences'],
      // Le taux d'absentéisme se relit après une présence corrigée (TF-476).
      ['absenteisme'],
      ['assiduite'],
      ['apprenants-risque'],
      ['ecf'],
      ['fin-formation'],
      ['tests-positionnement'],
      ['copie-test'],
      ['corrections'],
      ['dossier'],
      ['apprenants'],
    ]) {
      void client.invalidateQueries({ queryKey: cle });
    }
  };
}

export function useDemanderModification() {
  const apres = useApresGeste();
  return useMutation({
    mutationFn: (entree: EntreeDemande) =>
      appelApi<ResultatDemande>('/modifications-sensibles', { methode: 'POST', corps: entree }),
    onSuccess: () => apres(),
  });
}

export function useApprouverDemande() {
  const apres = useApresGeste();
  return useMutation({
    mutationFn: (id: string) =>
      appelApi<ResultatDemande>(`/modifications-sensibles/${id}/approbation`, { methode: 'POST' }),
    onSuccess: () => apres(),
  });
}

export function useRefuserDemande() {
  const apres = useApresGeste();
  return useMutation({
    mutationFn: ({ id, motif }: { id: string; motif: string }) =>
      appelApi<ResultatDemande>(`/modifications-sensibles/${id}/refus`, { methode: 'POST', corps: { motif } }),
    onSuccess: () => apres(),
  });
}

export function useRetirerDemande() {
  const apres = useApresGeste();
  return useMutation({
    mutationFn: (id: string) =>
      appelApi<ResultatDemande>(`/modifications-sensibles/${id}/retrait`, { methode: 'POST' }),
    onSuccess: () => apres(),
  });
}
